import React from 'react';
import './Flash.css';

const BINGO_COLUMNS = [
  { letter: 'B', numbers: [4, 11, 2, 14, 7] },
  { letter: 'I', numbers: [19, 28, 23, 17, 30] },
  { letter: 'N', numbers: [34, 45, 39, 31, 42] },
  { letter: 'G', numbers: [48, 56, 52, 60, 51] },
  { letter: 'O', numbers: [67, 72, 63, 75, 69] },
];

const CALLED = new Set([4, 11, 2, 19, 28, 23, 34, 45, 39, 48, 56, 67, 72, 63]);

const steps = [
  {
    number: '01',
    title: 'Build the session',
    copy: 'Organize games, patterns, prizes, and breaks before the first ball is called.',
  },
  {
    number: '02',
    title: 'Run the game',
    copy: 'Launch each scheduled game and keep the caller focused with a clear, purpose-built control view.',
  },
  {
    number: '03',
    title: 'Present it live',
    copy: 'Show called balls, the active pattern, and game information on a hall-ready display.',
  },
  {
    number: '04',
    title: 'Review a claim',
    copy: 'Use camera-based card reading and verification to help streamline the winner-review workflow.',
  },
];

const faqs = [
  {
    question: 'What equipment does BuzzBoard Flash need?',
    answer: 'BuzzBoard Flash is designed to work with familiar connected devices: a caller computer, a display for the hall, and a compatible phone or tablet when using the scanning workflow.',
  },
  {
    question: 'Can we use our own game schedule and patterns?',
    answer: 'Yes. Build a session around your hall’s game order, prizes, standard patterns, and custom patterns, then move through the schedule as the event runs.',
  },
  {
    question: 'Does the scanner automatically confirm every winner?',
    answer: 'The scanner is a camera-based reading and verification aid designed to make reviews faster and more consistent. Final review procedures should always follow your hall’s rules and operating policies.',
  },
  {
    question: 'Can the hall display match our event?',
    answer: 'Presentation themes let you tailor the live experience while keeping game status, called balls, and the current pattern easy to read from across the room.',
  },
  {
  question: 'Does BuzzBoard Flash require a specific bingo paper or serial numbers?',
  answer: 'No. BuzzBoard Flash is designed to work without requiring specific bingo paper, manufacturers, or serial numbers. Simply scan the card and Flash reads the card directly, allowing halls to keep using the paper they already know and trust.',
},
];

function StatusDot() {
  return <span className="flash-status-dot" aria-hidden="true" />;
}

function ArrowIcon() {
  return <span className="flash-arrow" aria-hidden="true">&#8599;</span>;
}

function MiniPattern() {
  const active = new Set([0, 4, 6, 8, 12, 16, 18, 20, 24]);
  return (
    <div className="flash-pattern" aria-label="X bingo pattern preview">
      {Array.from({ length: 25 }, (_, index) => (
        <span key={index} className={active.has(index) ? 'is-active' : ''} />
      ))}
    </div>
  );
}

function LiveBoard() {
  return (
    <div className="flash-board-shell">
      <div className="flash-board-topbar">
        <div className="flash-wordmark"><span>BUZZ</span>BOARD</div>
        <div className="flash-live-pill"><StatusDot /> LIVE</div>
      </div>
      <div className="flash-board-meta">
        <div>
          <span className="flash-label">NOW PLAYING</span>
          <strong>Game 07</strong>
          <small>Double Bingo &middot; $500</small>
        </div>
        <div className="flash-current-ball">
          <span>LAST CALL</span>
          <strong><em>G</em>52</strong>
        </div>
      </div>
      <div className="flash-board-grid">
        {BINGO_COLUMNS.map((column) => (
          <div className="flash-board-column" key={column.letter}>
            <b>{column.letter}</b>
            {column.numbers.map((number) => (
              <span key={number} className={CALLED.has(number) ? 'is-called' : ''}>{number}</span>
            ))}
          </div>
        ))}
      </div>
      <div className="flash-board-footer">
        <div><StatusDot /> 14 BALLS CALLED</div>
        <div>FOUR CORNERS</div>
      </div>
    </div>
  );
}

function Flash() {
  return (
    <main className="flash-page">
      <section className="flash-hero" id="top">
        <div className="flash-ambient flash-ambient-one" />
        <div className="flash-ambient flash-ambient-two" />
        <nav className="flash-nav" aria-label="BuzzBoard Flash page navigation">
          <a className="flash-brand" href="#top" aria-label="BuzzBoard Flash home">
            <span className="flash-brand-mark">B</span>
            <span>BUZZBOARD <b>FLASH</b></span>
          </a>
          <div className="flash-nav-links">
            <a href="#how-it-works">How it works</a>
            <a href="#features">Features</a>
            <a href="#faq">FAQ</a>
          </div>
          <a className="flash-button flash-button-small" href="#contact">Request a demo</a>
        </nav>

        <div className="flash-hero-grid">
          <div className="flash-hero-copy">
            <div className="flash-kicker"><StatusDot /> THE MODERN BINGO OPERATING SYSTEM</div>
            <h1>Run the room.<br /><span>Own the moment.</span></h1>
            <p>One connected workspace for building sessions, calling games, powering the live flashboard, and streamlining winner review.</p>
            <div className="flash-hero-actions">
              <a className="flash-button" href="#how-it-works">See how it works <ArrowIcon /></a>
              <a className="flash-text-link" href="#walkthrough"><span className="flash-play">&#9654;</span> Watch preview</a>
            </div>
            <div className="flash-trust-row">
              <span>BUILT FOR</span>
              <b>Bingo halls</b>
              <i />
              <b>Community clubs</b>
              <i />
              <b>Fundraisers</b>
            </div>
          </div>
          <div className="flash-hero-visual">
            <div className="flash-screen-glow" />
            <LiveBoard />
            <div className="flash-float-card flash-float-pattern">
              <MiniPattern />
              <div><span>ACTIVE PATTERN</span><strong>X Pattern</strong></div>
            </div>
            <div className="flash-float-card flash-float-sync">
              <span className="flash-check">&#10003;</span>
              <div><span>SESSION STATUS</span><strong>All displays synced</strong></div>
            </div>
          </div>
        </div>
      </section>

      <section className="flash-section flash-how" id="how-it-works">
        <div className="flash-section-heading">
          <div><span className="flash-overline">FROM SETUP TO SHOWTIME</span><h2>One smooth game-day flow.</h2></div>
          <p>BuzzBoard Flash keeps the information your team needs connected, clear, and ready at each stage of the event.</p>
        </div>
        <div className="flash-step-grid">
          {steps.map((step, index) => (
            <article className="flash-step" key={step.number}>
              <div className="flash-step-number">{step.number}</div>
              <div className="flash-step-icon" aria-hidden="true">{['+', '▶', '▦', '✓'][index]}</div>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
              {index < steps.length - 1 && <span className="flash-step-line" />}
            </article>
          ))}
        </div>
      </section>

      <section className="flash-section flash-features" id="features">
        <div className="flash-centered-heading">
          <span className="flash-overline">THE COMPLETE TOOLKIT</span>
          <h2>Purpose-built for every part of the game.</h2>
          <p>Powerful where it matters. Quiet everywhere else.</p>
        </div>

        <div className="flash-feature-row">
          <div className="flash-feature-copy">
            <div className="flash-feature-index">01 / LIVE FLASHBOARD</div>
            <h3>Every call. Clear across the room.</h3>
            <p>Give players an easy-to-read view of the current ball, complete called-number history, active game, and winning pattern.</p>
            <ul>
              <li><span>&#10003;</span> High-visibility called ball board</li>
              <li><span>&#10003;</span> Live game and pattern context</li>
              <li><span>&#10003;</span> Presentation-ready hall display</li>
            </ul>
          </div>
          <div className="flash-feature-visual flash-dashboard-demo">
            <div className="flash-demo-toolbar"><i /><i /><i /><span>HALL DISPLAY</span></div>
            <LiveBoard />
          </div>
        </div>

        <div className="flash-feature-row flash-feature-reverse">
          <div className="flash-feature-copy">
            <div className="flash-feature-index">02 / SESSION BUILDER</div>
            <h3>Plan the night before the doors open.</h3>
            <p>Build the entire program in one place, from the opening game through the final jackpot. Then launch or resume games as the session moves.</p>
            <a className="flash-inline-link" href="#contact">Explore session planning <ArrowIcon /></a>
          </div>
          <div className="flash-feature-visual flash-schedule-demo">
            <div className="flash-demo-header"><div><span>FRIDAY NIGHT</span><strong>Game Schedule</strong></div><button type="button">+ Add game</button></div>
            {[
              ['01', 'Early Bird', 'Straight Line', 'COMPLETED'],
              ['02', 'Lucky Corners', 'Four Corners', 'LIVE'],
              ['03', 'Double Trouble', 'Double Bingo', 'UP NEXT'],
              ['04', 'Flash Jackpot', 'Coverall', 'SCHEDULED'],
            ].map((game) => (
              <div className={`flash-game-row ${game[3] === 'LIVE' ? 'is-live' : ''}`} key={game[0]}>
                <span>{game[0]}</span><div><strong>{game[1]}</strong><small>{game[2]}</small></div><em>{game[3]}</em>
              </div>
            ))}
          </div>
        </div>

        <div className="flash-feature-row">
          <div className="flash-feature-copy">
            <div className="flash-feature-index">03 / SCAN &amp; REVIEW</div>
            <h3>A faster path from “Bingo” to review.</h3>
            <p>Camera-based card reading and verification is designed to help staff compare a claimed card with the active pattern and called-ball history.</p>
            <div className="flash-note"><span>i</span><p><strong>Built as a workflow aid.</strong> Results support your team’s review process and should be used alongside hall procedures.</p></div>
          </div>
          <div className="flash-feature-visual flash-scanner-demo">
            <div className="flash-scan-head"><span>BUZZBOARD SCAN</span><b><StatusDot /> GAME 02 LIVE</b></div>
            <div className="flash-scan-window">
              <div className="flash-scan-corner c1" /><div className="flash-scan-corner c2" /><div className="flash-scan-corner c3" /><div className="flash-scan-corner c4" />
              <div className="flash-card-mini">
                <strong>B I N G O</strong>
                {Array.from({ length: 25 }, (_, index) => <i key={index} className={[0, 4, 6, 8, 12, 16, 18, 20, 24].includes(index) ? 'is-daubed' : ''}>{index === 12 ? '*' : (index * 7) % 75 + 1}</i>)}
              </div>
              <span className="flash-scan-line" />
            </div>
            <div className="flash-scan-result"><span className="flash-check">&#10003;</span><div><small>READY FOR REVIEW</small><strong>Pattern detected</strong></div><em>X Pattern</em></div>
          </div>
        </div>

        <div className="flash-feature-row flash-feature-reverse">
          <div className="flash-feature-copy">
            <div className="flash-feature-index">04 / THEMES &amp; PRESENTATION</div>
            <h3>Your room. Your atmosphere.</h3>
            <p>Choose a presentation style that fits the event—from focused and traditional to energetic and celebratory—without sacrificing readability.</p>
            <div className="flash-swatches"><span className="theme-green" /><span className="theme-gold" /><span className="theme-blue" /><span className="theme-red" /><small>Display themes</small></div>
          </div>
          <div className="flash-feature-visual flash-theme-demo">
            <div className="flash-theme-card theme-preview-one"><span>CLASSIC NIGHT</span><strong>G<span>52</span></strong><small>FOUR CORNERS</small></div>
            <div className="flash-theme-card theme-preview-two"><span>NEON NIGHT</span><strong>O<span>69</span></strong><small>DOUBLE BINGO</small></div>
            <div className="flash-theme-selector"><span>Presentation</span><b>Theme 02 / Neon</b><div><i /><i /><i /><i /></div></div>
          </div>
        </div>
      </section>

      <section className="flash-section flash-video" id="walkthrough">
        <div className="flash-video-frame">
          <div className="flash-video-copy">
            <span className="flash-overline">FULL PRODUCT WALKTHROUGH</span>
            <h2>See the whole session in motion.</h2>
            <p>A guided tour is on the way. Soon, watch a complete flow from building the schedule to reviewing a winning card.</p>
            <span className="flash-coming-soon"><StatusDot /> VIDEO COMING SOON</span>
          </div>
          <button className="flash-video-play" type="button" aria-label="Product walkthrough coming soon" disabled><span>&#9654;</span></button>
          <div className="flash-video-timeline"><span /><i>00:00</i><i>03:24</i></div>
        </div>
      </section>

      <section className="flash-section flash-halls">
        <div className="flash-halls-intro">
          <span className="flash-overline">DESIGNED FOR REAL BINGO HALLS</span>
          <h2>Technology that respects the way your team works.</h2>
          <p>Built around the pace, visibility, and practical realities of a live room—not a generic event template.</p>
        </div>
        <div className="flash-benefit-grid">
          <article><span>01</span><h3>Simple under pressure</h3><p>Focused controls and clear status help callers move confidently through a busy session.</p></article>
          <article><span>02</span><h3>Works with familiar devices</h3><p>Use the computers, displays, phones, and tablets your staff already understands.</p></article>
          <article><span>03</span><h3>Built for visibility</h3><p>Strong contrast and large information make the game easier to follow across the room.</p></article>
          <article><span>04</span><h3>One connected workflow</h3><p>Keep the schedule, live game, hall display, and review context moving together.</p></article>
        </div>
      </section>

      <section className="flash-section flash-faq" id="faq">
        <div className="flash-faq-title"><span className="flash-overline">QUESTIONS, ANSWERED</span><h2>Everything you need to know.</h2><p>Still curious? We’d be happy to walk you through it.</p></div>
        <div className="flash-faq-list">
          {faqs.map((faq, index) => (
            <details key={faq.question} open={index === 0}>
              <summary>{faq.question}<span>+</span></summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="flash-cta" id="contact">
        <div className="flash-ambient flash-ambient-cta" />
        <span className="flash-overline">READY WHEN YOU ARE</span>
        <h2>Bring a better game night to your hall.</h2>
        <p>See how BuzzBoard Flash can fit your room, your team, and your event workflow.</p>
        <div className="flash-cta-actions"><a className="flash-button" href="#top">Request a demo <ArrowIcon /></a><a className="flash-text-link" href="#top">Contact K&amp;M Sales</a></div>
      </section>

      <footer className="flash-footer">
        <a className="flash-brand" href="#top"><span className="flash-brand-mark">B</span><span>BUZZBOARD <b>FLASH</b></span></a>
        <p>Modern tools for better bingo nights.</p>
        <span>&copy; {new Date().getFullYear()} BuzzBoard. All rights reserved.</span>
      </footer>
    </main>
  );
}

export default Flash;